import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useDropzone } from "react-dropzone";
import { Image } from "react-bootstrap";

import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Card,
  Table,
  Modal,
} from "react-bootstrap";
import {
  checkFile,
  deleteFile,
  getDrivePrintFiles,
  getDriveSelectedDate,
  getUserFiles,
  uploadToDrive,
  uploadToServer,
} from "../../../api/drive-service";
import { toast } from "../../../helpers/swal";
import { getDateDayMountYear } from "../../../helpers/functions/date-time";
import "./home.scss";
import "react-datepicker/dist/react-datepicker.css";
import DatePicker from "react-datepicker";
import Lottie from "lottie-react";
import searchAnimation from "../../../helpers/animations/searching.json";
import fileTransferAnimation from "../../../helpers/animations/fileTransfer.json";
import doneAnimation from "../../../helpers/animations/done.json";
import { settings } from "../../../helpers/settings";
import { Link } from "react-router-dom";

const PREVIEW_PREFERENCE_KEY = "showImagePreview";

const Home = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedLocation, setSelectedLocation] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [listFolders, setListFolders] = useState([]);
  const [folderId, setFolderId] = useState("");
  const [fileLocation, setFileLocation] = useState([]);
  const [deletedFiles, setDeletedFiles] = useState([]);
  const [selectedFileId, setSelectedFileId] = useState("");
  const [uploading, setUploading] = useState(false);
  const [isLoadingFolders, setIsLoadingFolders] = useState(false);
  const [userFiles, setUserFiles] = useState([]);
  const [show, setShow] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [showPreview, setShowPreview] = useState(true); // Default true - preview göster
  const [fileProcessing, setFileProcessing] = useState(false); // Yeni: Dosya işleme durumu
  const API_URL = settings.apiURL;

  // Utils
  const sortByLeadingNumberThenName = (a, b) => {
    const num = (x) => {
      const m = String(x?.name || x).match(/^\s*(\d+)/);
      return m ? parseInt(m[1], 10) : Number.POSITIVE_INFINITY;
    };
    const na = num(a),
      nb = num(b);
    if (na !== nb) return na - nb;
    return (a.name || "").localeCompare(b.name || "", "en", {
      numeric: true,
      sensitivity: "base",
    });
  };

  // Modal handlers
  const handleClose = () => {
    setShow(false);
    setUploadComplete(false);
  };

  const handleShow = useCallback(() => {
    setShow(true);
    setUploadComplete(false);
  }, []);

  const turkishCharsRegex = /[çğıöşüÇĞİÖŞÜ]/;

  // Initialize with today's date
  useEffect(() => {
    const today = new Date();
    setSelectedDate(today);
  }, []);

  // Load preview preferences from localStorage
  useEffect(() => {
    const savedPreference = localStorage.getItem(PREVIEW_PREFERENCE_KEY);
    // Eğer kayıtlı değer varsa kullan, yoksa default true
    setShowPreview(
      savedPreference !== null ? savedPreference === "true" : true
    );
  }, []);

  // Preview toggle handler
  const handlePreviewToggle = (checked) => {
    setShowPreview(checked);
    localStorage.setItem(PREVIEW_PREFERENCE_KEY, checked.toString());
  };

  // Create image preview
  const createImagePreview = (file) => {
    return new Promise((resolve) => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = new window.Image();

      img.onload = () => {
        const maxWidth = 300;
        const maxHeight = 200;
        let { width, height } = img;

        if (width > height) {
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/png", 0.7));
        URL.revokeObjectURL(img.src); // ✅ burada revoke
      };

      img.src = URL.createObjectURL(file);
    });
  };

  // Dropzone
  const onDrop = async (acceptedFiles, rejectedFiles) => {
    if (rejectedFiles.length > 0) {
      rejectedFiles.forEach((file) => {
        toast(
          `${file.file.name} is not a valid file type! Only PNG files are allowed.`,
          "warning"
        );
      });
    }

    // Sadece PNG dosyaları al
    let pngFiles = acceptedFiles.filter((file) => file.type === "image/png");
    if (pngFiles.length === 0) {
      toast("Please upload a PNG file.", "warning");
      return;
    }

    // 🚩 Türkçe karakter kontrolü
    const invalidFiles = pngFiles.filter((file) =>
      turkishCharsRegex.test(file.name)
    );
    if (invalidFiles.length > 0) {
      invalidFiles.forEach((file) => {
        toast(
          `${file.name} contains Turkish characters. Please rename the file.`,
          "error"
        );
      });

      // Geçersiz dosyaları çıkar
      pngFiles = pngFiles.filter((f) => !invalidFiles.includes(f));
    }

    if (pngFiles.length === 0) {
      return;
    }

    // Mevcut dosya adlarını al (duplicate kontrolü için)
    const existingFileNames = uploadedFiles.map((item) => item.file.name);

    // Yeni dosyaları filtrele (duplicate olanları çıkar)
    const newFiles = pngFiles.filter(
      (file) => !existingFileNames.includes(file.name)
    );

    if (newFiles.length === 0) {
      toast("All selected files are already in the list.", "warning");
      return;
    }

    // Yeni: Dosya işleme başladı
    setFileProcessing(true);

    // Yeni dosyaları hazırlayıp listeye ekle
    const entries = await Promise.all(
      newFiles.map(async (file, index) => {
        const objectUrl = URL.createObjectURL(file);
        const preview = showPreview ? await createImagePreview(file) : null;
        return {
          file,
          preview, // dataURL olarak
          objectUrl, // revoke için
          id: Date.now() + index,
        };
      })
    );

    setUploadedFiles((prevFiles) => [...prevFiles, ...entries]);

    // Yeni: Dosya işleme tamamlandı
    setFileProcessing(false);

    if (newFiles.length < pngFiles.length) {
      const duplicateCount = pngFiles.length - newFiles.length;
      toast(
        `${duplicateCount} file(s) were already in the list and skipped.`,
        "info"
      );
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: { "image/png": [".png"] },
    onDropRejected: () => {
      toast("Only PNG files are allowed.", "warning");
    },
  });

  // ObjectURL cleanup
  useEffect(() => {
    return () => {
      uploadedFiles.forEach((item) => {
        if (item.objectUrl) URL.revokeObjectURL(item.objectUrl);
      });
    };
  }, [uploadedFiles]);

  // Get Google APIs token
  const getGoogleApisToken = async () => {
    try {
      const response = await axios.get(`${API_URL}/generateApiToken.php`);

      if (response.data.access_token) {
        sessionStorage.setItem("googleApisToken", response.data.access_token);
        sessionStorage.setItem(
          "googleRefreshToken",
          response.data.refresh_token
        );
      } else {
        toast("Error: " + response.data.error, "error");
      }
    } catch (err) {
      toast("Error: " + err.message, "error");
    }
  };

  // Fetch drive files (folders)
  const fetchDriveFiles = async () => {
    try {
      const respDriveFiles = await getDrivePrintFiles();
      setListFolders(respDriveFiles.data);
    } catch (err) {
      const message = err.response ? err.response.data.message : err.message;
      toast(message, "error");
    }
  };

  // Initialize token and fetch folders
  useEffect(() => {
    const initializeData = async () => {
      await getGoogleApisToken();
      await fetchDriveFiles();
    };
    initializeData();
  }, []);

  // Handle date selection and find corresponding folder
  const handleDateSelection = useCallback(() => {
    if (!selectedDate || listFolders.length === 0) return;

    const selectedFormattedDate = getDateDayMountYear(selectedDate);
    if (selectedFormattedDate) {
      const selectedFolder = listFolders.find(
        (folder) => folder.name === selectedFormattedDate
      );
      if (selectedFolder) {
        setFolderId(selectedFolder.id);
      } else {
        setFolderId("");
        setFileLocation([]);
        toast(`No folder found for date: ${selectedFormattedDate}`, "info");
      }
    }
  }, [selectedDate, listFolders]);

  // Trigger date selection when date or folders change
  useEffect(() => {
    handleDateSelection();
  }, [handleDateSelection]);

  // Get files for selected date
  const getDriveSelectDate = async () => {
    if (!folderId) return;

    setIsLoadingFolders(true);
    try {
      const resp = await getDriveSelectedDate(folderId);
      const sortedFiles = [...resp.data].sort(sortByLeadingNumberThenName);
      setFileLocation(sortedFiles);
    } catch (err) {
      const message = err.response ? err.response.data.message : err.message;
      toast(message, "error");
      setFileLocation([]);
    } finally {
      setIsLoadingFolders(false);
    }
  };

  // Fetch files when folderId changes
  useEffect(() => {
    getDriveSelectDate();
  }, [folderId]);

  // Handle date change
  const handleDateChange = (date) => {
    setSelectedDate(date);
    setSelectedLocation(""); // Reset selected location when date changes
  };
  const uploadFileInChunks = async (
    file,
    user_id,
    folderName,
    driveDateFolder
  ) => {
    const chunkSize = 20 * 1024 * 1024; // 20MB
    const totalChunks = Math.ceil(file.size / chunkSize);
    const uploadId = Date.now() + "-" + file.name;

    let finalResponse = null;

    for (let i = 0; i < totalChunks; i++) {
      const start = i * chunkSize;
      const end = Math.min(start + chunkSize, file.size);
      const chunk = file.slice(start, end);

      const formData = new FormData();
      formData.append("file", chunk);
      formData.append("fileName", file.name);
      formData.append("chunkIndex", i);
      formData.append("totalChunks", totalChunks);
      formData.append("uploadId", uploadId);
      formData.append("user_id", user_id);
      formData.append("folderName", folderName);
      formData.append("driveDateFolder", driveDateFolder);

      try {
        const resp = await axios.post(`${API_URL}/upload_chunk.php`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        // If it's the last chunk, we get the final response with temp_path and temp_id
        if (resp.data.success && resp.data.temp_path) {
          finalResponse = resp.data;
        }
      } catch (err) {
        console.error("Chunk upload failed:", err);
        throw new Error(`Chunk ${i + 1}/${totalChunks} failed: ${err.message}`);
      }
    }

    return finalResponse;
  };

  // Upload handler
  const handleUpload = async () => {
    if (!selectedLocation) {
      toast("No folder location selected", "warning");
      return;
    }

    const selectedFolder = fileLocation.find(
      (f) => f.name === selectedLocation
    );
    if (!selectedFolder) {
      toast("No folder found for selected location", "warning");
      return;
    }

    const user_id = localStorage.getItem("user_id");
    const selectedFormattedDate = getDateDayMountYear(selectedDate);

    // Pre-check for duplicates
    let results;
    try {
      results = await precheckNamesBatch({
        userId: user_id,
        folderName: selectedFolder.name,
        driveDateFolder: selectedFormattedDate,
        files: uploadedFiles,
      });
    } catch (err) {
      toast(err.message || "Pre-check failed.", "error");
      return;
    }

    const duplicates = results
      .filter((r) => !r.available)
      .map((r) => r.file_name);
    if (duplicates.length > 0) {
      toast(
        `Already exists: ${duplicates.join(", ")}. Please rename.`,
        "warning"
      );
      return;
    }

    // Start upload process
    setSelectedFileId(selectedFolder.id);
    handleShow();
    setUploading(true);

    try {
      await Promise.all(
        uploadedFiles.map(async ({ file }) => {
          try {
            // 1. Upload file in chunks
            const resp = await uploadFileInChunks(
              file,
              user_id,
              selectedFolder.name,
              selectedFormattedDate
            );

            if (!resp || !resp.temp_path || !resp.temp_id) {
              throw new Error("Chunk upload failed, no response from server");
            }

            const temp_path = resp.temp_path;
            const temp_id = resp.temp_id;

            // 2. Upload to Google Drive
            const respDrive = await uploadToDrive(
              temp_id,
              temp_path,
              selectedFolder.id,
              selectedFolder.name
            );

            // 3. Check and finalize
            await checkFile(temp_id, respDrive.data.file_id, temp_path);
            toast(`${file.name} uploaded successfully!`, "success");
          } catch (error) {
            if (error.response?.status === 409) {
              toast(
                `Duplicate detected during upload: ${file.name}. Please rename and try again.`,
                "error"
              );
            } else {
              console.error(`Upload error for ${file.name}:`, error);
              toast(`Error uploading ${file.name}: ${error.message}`, "error");
            }
          }
        })
      );

      setUploadComplete(true);
      setTimeout(() => handleClose(), 2000);
      setUploadedFiles([]);
      await getUserFilesData();
    } finally {
      setUploading(false);
    }
  };

  // Get user files
  const getUserFilesData = async () => {
    try {
      const user_id = localStorage.getItem("user_id");
      const user_files = await getUserFiles(user_id);
      setUserFiles(user_files.data);
    } catch (err) {
      const message = err.response ? err.response.data.message : err.message;
      toast(message, "error");
    }
  };

  // Load user files on component mount
  useEffect(() => {
    getUserFilesData();
  }, []);

  // Handle file deletion
  const handleDelete = (fileId, index) => {
    if (fileId) {
      // Delete from server
      deleteFile(fileId)
        .then((response) => {
          if (response.status === 200) {
            setDeletedFiles((prevDeleted) => [...prevDeleted, fileId]);
            getUserFilesData();
            toast("File deleted successfully!", "success");
          } else {
            toast("Error occurred while deleting the file.", "error");
          }
        })
        .catch((error) => {
          console.error("Error deleting file:", error);
          toast("An error occurred while deleting the file.", "error");
        });
    } else {
      // Remove from uploaded files list
      setUploadedFiles((prevFiles) => {
        if (index >= 0 && index < prevFiles.length) {
          const toRemove = prevFiles[index];
          if (toRemove?.objectUrl) URL.revokeObjectURL(toRemove.objectUrl);
          return prevFiles.filter((_, i) => i !== index);
        }
        return prevFiles;
      });
    }
  };

  // Pre-check function for batch file validation
  async function precheckNamesBatch({
    userId,
    folderName,
    driveDateFolder,
    files,
  }) {
    const file_names = files.map(({ file }) => file.name);

    const resp = await fetch(`${API_URL}/check_files_available_batch.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        user_id: userId,
        folderName,
        driveDateFolder,
        file_names,
      }),
    });

    if (!resp.ok) {
      const t = await resp.json().catch(() => ({}));
      throw new Error(
        t?.message || `Pre-check failed with status ${resp.status}`
      );
    }
    const data = await resp.json();
    return data.results || [];
  }

  return (
    <Container className="mayaup-home" style={{ maxWidth: "100%" }}>
      <div className="mt-2 mb-3 text-center">
        <Button
          as={Link}
          to="/home"
          variant="warning"
          size="sm"
          style={{
            fontWeight: "bold",
            color: "white",
            padding: "5px 15px",
            width: "auto",
            display: "inline-block",
          }}
        >
          Home
        </Button>
      </div>

      {/* Preview Checkbox */}
      <Row className="justify-content-center mb-3">
        <Col md={10}>
          <Form.Check
            type="checkbox"
            id="preview-toggle"
            label="Hide image previews"
            checked={!showPreview} // Ters mantık, işaretli ise preview gizlensin
            onChange={(e) => handlePreviewToggle(!e.target.checked)} // Ters çevir
          />

          <Form.Text className="text-muted">
            Enable this option to hide image previews and show only file names
          </Form.Text>
        </Col>
      </Row>

      <Row className="justify-content-center">
        <Col md={10}>
          <Card>
            <Card.Body>
              <Form className="p-4">
                <Row className="row">
                  {/* CALENDAR */}
                  <Col className="calendar-col">
                    <Form.Group controlId="formDate">
                      <Form.Label>Date Selection:</Form.Label>
                      <div className="calendar-wrapper">
                        <DatePicker
                          selected={selectedDate}
                          onChange={handleDateChange}
                          minDate={
                            new Date(
                              new Date().setDate(new Date().getDate() - 1)
                            )
                          }
                          maxDate={
                            new Date(
                              new Date().setDate(new Date().getDate() + 2)
                            )
                          }
                          inline
                          calendarClassName="custom-calendar"
                        />
                      </div>
                    </Form.Group>
                  </Col>

                  {/* FOLDER BOXES */}
                  <Col className="file-location-col">
                    <Form.Group controlId="formLocation">
                      <Form.Label style={{ marginBottom: "10px" }}>
                        File Location Selection:
                      </Form.Label>
                      <div className="folder-grid">
                        {isLoadingFolders ? (
                          <div className="loading-animation-wrapper">
                            <Lottie
                              animationData={searchAnimation}
                              loop={true}
                              style={{ width: 720, height: 220 }}
                            />
                          </div>
                        ) : fileLocation.length > 0 ? (
                          fileLocation.map((location) => (
                            <div
                              key={location.id}
                              onClick={() => setSelectedLocation(location.name)}
                              className={`folder-item ${
                                selectedLocation === location.name
                                  ? "selected"
                                  : ""
                              }`}
                            >
                              {location.name}
                            </div>
                          ))
                        ) : (
                          <div className="no-folders-message">
                            <p>No folders found for selected date</p>
                          </div>
                        )}
                      </div>
                    </Form.Group>
                  </Col>
                </Row>

                {/* FILE UPLOAD */}
                <div
                  {...getRootProps()}
                  style={dropzoneStyle}
                  className="mt-4 text-center"
                >
                  <input {...getInputProps()} />
                  <p>Upload files by drag and drop or click</p>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Uploaded Files List */}
      {uploadedFiles.length > 0 && (
        <Row className="mt-4">
          <Col md={12}>
            <h4>Files to Upload:</h4>

            {/* Yeni: Dosya işleme animasyonu */}
            {fileProcessing && (
              <div className="text-center mb-3">
                <Lottie
                  animationData={fileTransferAnimation}
                  loop={true}
                  style={{ width: 150, height: 150, margin: "0 auto" }}
                />
                <p>Processing files... Please wait</p>
              </div>
            )}

            <div style={{ display: "flex", flexWrap: "wrap", gap: "15px" }}>
              {uploadedFiles.map((item, index) => (
                <div
                  key={item.id || index}
                  style={{
                    position: "relative",
                    border: "1px solid #ddd",
                    borderRadius: "8px",
                    padding: "10px",
                    backgroundColor: "#f9f9f9",
                    minWidth: "200px",
                    maxWidth: "300px",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  }}
                >
                  <button
                    onClick={() => handleDelete(null, index)}
                    style={{
                      position: "absolute",
                      top: "5px",
                      right: "5px",
                      cursor: "pointer",
                      background: "#dc3545",
                      color: "white",
                      border: "none",
                      borderRadius: "50%",
                      width: "25px",
                      height: "25px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "16px",
                      fontWeight: "bold",
                      zIndex: 10,
                    }}
                    title="Remove file"
                  >
                    ×
                  </button>

                  {/* Dosya önizlemesini burada kontrol et */}
                  {showPreview ? (
                    <Image
                      src={item.preview}
                      alt={item.file.name}
                      style={{
                        width: "100%",
                        height: "auto",
                        maxHeight: "200px",
                        objectFit: "contain",
                        borderRadius: "4px",
                        marginBottom: "10px",
                        display: "block",
                      }}
                    />
                  ) : (
                    <div>
                      <p>{item.file.name}</p>
                      <p>
                        {Math.round((item.file.size / 1024 / 1024) * 100) / 100}{" "}
                        MB
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Col>
        </Row>
      )}

      {/* Upload Button */}
      <Row className="mt-4 justify-content-center">
        <Col md={10} className="text-center">
          <Button
            variant="primary"
            onClick={handleUpload}
            disabled={
              uploading ||
              uploadedFiles.length === 0 ||
              !selectedLocation ||
              fileProcessing // Yeni: Dosya işlenirken buton disabled
            }
            style={{
              opacity:
                uploadedFiles.length === 0 ||
                !selectedLocation ||
                fileProcessing
                  ? 0.6
                  : 1,
              cursor:
                uploadedFiles.length === 0 ||
                !selectedLocation ||
                fileProcessing
                  ? "not-allowed"
                  : "pointer",
            }}
          >
            {fileProcessing ? (
              <>
                <span>Processing Files...</span>
              </>
            ) : uploading ? (
              "Uploading..."
            ) : (
              `Send Files (${uploadedFiles.length})`
            )}
          </Button>

          {/* Yeni: Dosya işleme durum bilgisi */}
          {fileProcessing && (
            <div className="mt-2">
              <small className="text-muted">
                Please wait while files are being processed...
              </small>
            </div>
          )}
        </Col>
      </Row>

      {/* User Files Table */}
      <Row className="justify-content-center mt-4">
        <Col md={10}>
          <Card>
            <Card.Body>
              <h3 className="text-center mb-4">Today's Uploaded Files</h3>
              {userFiles.length > 0 ? (
                <Table
                  striped
                  bordered
                  hover
                  responsive
                  style={{ textAlign: "center" }}
                >
                  <thead>
                    <tr>
                      <th style={{ whiteSpace: "nowrap", minWidth: "200px" }}>
                        File Name
                      </th>
                      <th style={{ whiteSpace: "nowrap", minWidth: "50px" }}>
                        Uploaded Time
                      </th>
                      <th style={{ whiteSpace: "nowrap", minWidth: "100px" }}>
                        File Size
                      </th>
                      <th style={{ whiteSpace: "nowrap", minWidth: "50px" }}>
                        Folder Name
                      </th>
                      <th style={{ whiteSpace: "nowrap", minWidth: "100px" }}>
                        Dimensions (inches)
                      </th>
                      <th style={{ whiteSpace: "nowrap", minWidth: "100px" }}>
                        Upload Status
                      </th>
                      <th style={{ whiteSpace: "nowrap", minWidth: "10px" }}>
                        Actions
                      </th>
                      <th style={{ whiteSpace: "nowrap", minWidth: "100px" }}>
                        File Status
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {userFiles.map((file, index) => (
                      <tr key={index}>
                        <td
                          style={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {file.file_name}
                        </td>
                        <td>{file.uploaded_time}</td>
                        <td>{file.file_size}</td>
                        <td>{file.folderName}</td>
                        <td>
                          {file.inch_width?.toFixed(1)} x{" "}
                          {file.inch_height?.toFixed(1)}
                        </td>
                        <td
                          style={{
                            backgroundColor:
                              file.deleted == 1
                                ? "orange"
                                : file.success == 1
                                ? "lightgreen"
                                : "red",
                          }}
                        >
                          {file.deleted == 1
                            ? "DELETED"
                            : file.success == 1
                            ? "DONE"
                            : "ERROR"}
                        </td>
                        <td>
                          {file.deleted != 1 &&
                            file.success != "ERROR" &&
                            (!file.IsPrinted ||
                              file.IsPrinted === "Not Printed") && (
                              <button
                                onClick={() =>
                                  handleDelete(file.file_id, index)
                                }
                              >
                                Delete
                              </button>
                            )}
                        </td>
                        <td
                          style={{
                            backgroundColor:
                              file.deleted == 1
                                ? "orange"
                                : file.IsPrinted &&
                                  file.IsPrinted !== "Not Printed"
                                ? "lightgreen"
                                : "red",
                            color: "white",
                            fontWeight: "bold",
                            textAlign: "center",
                          }}
                        >
                          {file.deleted == 1
                            ? "Deleted"
                            : file.IsPrinted && file.IsPrinted !== "Not Printed"
                            ? "Printed"
                            : "Not Printed"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <div className="text-center">
                  <p>No files uploaded today</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Upload Progress Modal */}
      <Modal
        centered
        size="lg"
        show={show}
        onHide={handleClose}
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header>
          <Modal.Title>
            {uploadComplete ? "Upload Complete!" : "Please Wait..."}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {uploadComplete ? (
            <div>
              <Lottie
                animationData={doneAnimation}
                loop={false}
                style={{ width: 200, height: 200, margin: "0 auto" }}
              />
              <p
                style={{
                  fontSize: "18px",
                  color: "#28a745",
                  fontWeight: "bold",
                }}
              >
                All files uploaded successfully!
              </p>
            </div>
          ) : (
            <div>
              <Lottie
                animationData={fileTransferAnimation}
                loop={true}
                style={{ width: 300, height: 200, margin: "0 auto" }}
              />
              <p style={{ fontSize: "16px", marginTop: "20px" }}>
                Files are uploading...
              </p>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </Container>
  );
};

const dropzoneStyle = {
  border: "2px dashed #cccccc",
  borderRadius: ".3rem",
  padding: "20px",
  cursor: "pointer",
};

export default Home;
